import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useBackgroundMusic } from '../contexts/BackgroundMusicContext'
import { ModuleName, SfxTrack } from '../enums/enums'
import { getSfxUrl } from '../utils/audio'

/**
 * Milliseconds between the GO sound and the wrong-answer sound (DMVP-1035). The
 * ticket leaves the choice between immediate and a short silence open, so it is a
 * constant rather than a literal. Zero means the wrong-answer sound starts as soon
 * as the click is handled; the two overlap, which the audio pool supports.
 */
export const WRONG_ANSWER_DELAY_MS = 0

export interface McqSoundFx {
  playSubmit: () => void
  playReset: () => void
  playOptionSelect: () => void
  playWrongAnswer: () => void
  playRightAnswer: () => void
}

/**
 * The six MCQ sounds, for both the standard (Unity) and accessible views.
 *
 * Muting is handled one level down by playSoundFx, which respects the user's
 * background-music preference.
 *
 * @param levelName - the active module, e.g. ModuleName.Response. Picks the
 *   option-select sound: Response gets its own (DMVP-1034), everything else gets
 *   the default (DMVP-1033).
 */
export const useMcqSoundFx = (levelName: string): McqSoundFx => {
  const bgMusic = useBackgroundMusic()

  const urls = useMemo(() => ({
    submit: getSfxUrl(SfxTrack.McqSubmit),
    reset: getSfxUrl(SfxTrack.McqReset),
    optionSelect: getSfxUrl(
      levelName === ModuleName.Response ? SfxTrack.McqOptionSelectResponse : SfxTrack.McqOptionSelect
    ),
    wrongAnswer: getSfxUrl(SfxTrack.McqWrongAnswer),
    rightAnswer: getSfxUrl(SfxTrack.QuestionCorrect)
  }), [levelName])

  // Cleared on unmount so a pending wrong-answer sound cannot fire after the game
  // has gone away.
  const wrongAnswerTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (wrongAnswerTimer.current !== null) clearTimeout(wrongAnswerTimer.current)
    }
  }, [])

  const play = useCallback((url: string): void => {
    bgMusic.playSoundFx(url)
  }, [bgMusic])

  const playSubmit = useCallback((): void => play(urls.submit), [play, urls.submit])
  const playReset = useCallback((): void => play(urls.reset), [play, urls.reset])
  const playOptionSelect = useCallback((): void => play(urls.optionSelect), [play, urls.optionSelect])

  const playWrongAnswer = useCallback((): void => {
    if (WRONG_ANSWER_DELAY_MS <= 0) {
      bgMusic.playSoundFx(urls.wrongAnswer)
      return
    }
    if (wrongAnswerTimer.current !== null) clearTimeout(wrongAnswerTimer.current)
    wrongAnswerTimer.current = setTimeout(() => {
      wrongAnswerTimer.current = null
      bgMusic.playSoundFx(urls.wrongAnswer)
    }, WRONG_ANSWER_DELAY_MS)
  }, [bgMusic, urls.wrongAnswer])

  // Unlike the wrong-answer sound (DMVP-1035), the ticket for this one (DMVP-1103) does not leave the
  // timing open - it always plays immediately after GO - so there is no equivalent delay constant here.
  const playRightAnswer = useCallback((): void => play(urls.rightAnswer), [play, urls.rightAnswer])

  return { playSubmit, playReset, playOptionSelect, playWrongAnswer, playRightAnswer }
}
